import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import LegacyPlanComparison from './LegacyPlanComparison';
import DigitalAssetManagerModal from '../Modals/Planning/DigitalAssetManagerModal';
import CoAdminRolesPanel from './CoAdminRolesPanel';
import { writeFetch, getSession } from '../../api';

const {
  FiShield, FiClock, FiUserPlus, FiTrash2, FiAlertTriangle, FiCheck, FiInfo,
  FiX, FiAlertCircle, FiMail, FiBell, FiPlus, FiUser, FiSend, FiActivity,
  FiUserCheck, FiRefreshCw, FiLock, FiDatabase, FiCpu, FiBriefcase,
  FiToggleLeft, FiToggleRight, FiCheckCircle, FiXCircle
} = FiIcons;

const DEFAULT_ASSETS = {
  banking: [
    { id: 1, name: 'Chase Checking', account: '**** 4291', status: 'Verified' },
    { id: 2, name: 'HSBC Savings', account: '**** 8830', status: 'Pending' }
  ],
  social: [
    { id: 3, name: 'Facebook (Legacy)', handle: '@john.doe', status: 'Memorialized' },
    { id: 4, name: 'Instagram', handle: '@jdoe_life', status: 'Active' }
  ],
  investments: [
    { id: 5, name: 'Vanguard 401k', type: 'Retirement', status: 'Linked' },
    { id: 6, name: 'Robinhood', type: 'Equities', status: 'Linked' }
  ]
};

// ── Tribute Approval Toggle Component ──────────────────────────────────────
const TributeApprovalToggle = ({ memorial }) => {
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pageDbId, setPageDbId] = useState(null);

  // Load current setting from DB
  useEffect(() => {
    const load = async () => {
      try {
        const user = getSession();
        if (!user) return;
        const res = await writeFetch(`read/memorial_pages?user_id=${user.id}`);
        const data = await res.json();
        if (data.status === 'success' && data.data?.length) {
          // find the page matching this memorial
          const page = data.data.find(p =>
            p.slug === memorial.id || p.id === memorial.dbPageId
          ) || data.data[0];
          if (page) {
            setPageDbId(page.id);
            // tribute_approval: 1 = requires approval (ON), 0 = auto-approve (OFF)
            setRequiresApproval(page.tribute_approval !== 0);
          }
        }
      } catch {}
    };
    load();
  }, [memorial.id]);

  const handleToggle = async () => {
    const newVal = !requiresApproval;
    setRequiresApproval(newVal);
    setSaving(true);
    try {
      if (pageDbId) {
        await writeFetch(`update/memorial_pages/${pageDbId}`, {
          method: 'PUT',
          body: JSON.stringify({ tribute_approval: newVal ? 1 : 0 }),
        });
      }
      // Also persist in localStorage for immediate UI use
      localStorage.setItem(`tribute_approval_${memorial.id}`, newVal ? '1' : '0');
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark overflow-hidden relative">
      <div className="absolute top-0 right-0 w-40 h-40 bg-accent-gold/5 rounded-full -mr-20 -mt-20 blur-3xl" />

      <div className="flex items-center gap-4 mb-8">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-colors ${requiresApproval ? 'bg-accent-gold/10 text-accent-gold' : 'bg-green-50 text-green-500'}`}>
          <SafeIcon icon={requiresApproval ? FiShield : FiCheckCircle} className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-2xl font-serif text-accent-navy">Tribute Approval</h3>
          <p className="text-sm text-text-muted font-light">Control how incoming tributes are published to this memorial.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 items-start">
        {/* Toggle Control */}
        <div>
          <div className="flex items-center justify-between p-6 bg-brand-light/40 rounded-[28px] border-2 border-brand-dark mb-6">
            <div>
              <p className="text-sm font-black text-accent-navy uppercase tracking-widest mb-1">
                {requiresApproval ? 'Manual Approval' : 'Auto-Approve'}
              </p>
              <p className="text-xs text-text-muted leading-relaxed">
                {requiresApproval
                  ? 'New tributes are held for your review before appearing publicly.'
                  : 'All tributes are instantly published without review.'}
              </p>
            </div>

            <button
              onClick={handleToggle}
              disabled={saving}
              className={`relative w-16 h-8 rounded-full transition-all duration-300 flex-shrink-0 ml-4 ${requiresApproval ? 'bg-accent-gold' : 'bg-green-400'} ${saving ? 'opacity-60' : ''}`}
              aria-checked={requiresApproval}
              role="switch"
            >
              <span className={`absolute top-1.5 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 ${requiresApproval ? 'left-9' : 'left-1.5'}`} />
            </button>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-3">
            {saving ? (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <div className="w-3 h-3 border-2 border-accent-gold/30 border-t-accent-gold rounded-full animate-spin" />
                Saving…
              </div>
            ) : saved ? (
              <div className="flex items-center gap-2 text-xs text-green-600 font-bold">
                <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                Setting saved successfully
              </div>
            ) : null}
          </div>
        </div>

        {/* Mode description cards */}
        <div className="space-y-4">
          <div className={`p-5 rounded-[24px] border-2 transition-all ${requiresApproval ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark bg-white opacity-60'}`}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-accent-gold/10 text-accent-gold rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiShield} className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-accent-navy mb-1">ON — Manual Approval <span className="text-accent-gold">(Default)</span></p>
                <p className="text-[11px] text-text-muted leading-relaxed font-light">
                  Each tribute is queued in your moderation inbox. You review and approve or decline each one before it appears on the memorial.
                </p>
              </div>
            </div>
          </div>

          <div className={`p-5 rounded-[24px] border-2 transition-all ${!requiresApproval ? 'border-green-400 bg-green-50' : 'border-brand-dark bg-white opacity-60'}`}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 text-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-accent-navy mb-1">OFF — Auto-Approve</p>
                <p className="text-[11px] text-text-muted leading-relaxed font-light">
                  All tributes are published immediately. Best for trusted communities. You can still remove inappropriate content at any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending queue hint when approval is ON */}
      {requiresApproval && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 p-5 bg-accent-navy/[0.03] rounded-2xl border border-dashed border-brand-dark flex items-start gap-4"
        >
          <SafeIcon icon={FiInfo} className="w-5 h-5 text-accent-navy/40 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-text-muted leading-relaxed">
            Pending tributes can be reviewed from the <strong className="text-accent-navy">Admin → Moderation Queue</strong> tab. Approved tributes are immediately visible to all visitors.
          </p>
        </motion.div>
      )}
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────
const TabLegacySettings = ({ memorial }) => {
  const [retentionPlan, setRetentionPlan] = useState('eternal');

  const [subscribers, setSubscribers] = useState(() => {
    const saved = localStorage.getItem(`memorial_subscribers_${memorial.id}`);
    return saved ? JSON.parse(saved) : [];
  });

  const [activityLog, setActivityLog] = useState(() => {
    const saved = localStorage.getItem(`memorial_activity_log_${memorial.id}`);
    return saved ? JSON.parse(saved) : [];
  });

  const [currentSuccessor, setCurrentSuccessor] = useState(() => {
    const saved = localStorage.getItem(`memorial_successor_${memorial.id}`);
    return saved ? JSON.parse(saved) : null;
  });

  const [currentExecutor, setCurrentExecutor] = useState(() => {
    const saved = localStorage.getItem(`memorial_executor_${memorial.id}`);
    return saved ? JSON.parse(saved) : null;
  });

  const [assets, setAssets] = useState(() => {
    const saved = localStorage.getItem(`memorial_assets_${memorial.id}`);
    return saved ? JSON.parse(saved) : DEFAULT_ASSETS;
  });

  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [showSuccessorModal, setShowSuccessorModal] = useState(false);
  const [successorEmail, setSuccessorEmail] = useState('');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [showExecutorModal, setShowExecutorModal] = useState(false);
  const [executorEmail, setExecutorEmail] = useState('');
  const [isAssetManagerOpen, setIsAssetManagerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(`memorial_subscribers_${memorial.id}`, JSON.stringify(subscribers));
  }, [subscribers, memorial.id]);

  useEffect(() => {
    localStorage.setItem(`memorial_activity_log_${memorial.id}`, JSON.stringify(activityLog));
  }, [activityLog, memorial.id]);

  useEffect(() => {
    if (currentSuccessor) localStorage.setItem(`memorial_successor_${memorial.id}`, JSON.stringify(currentSuccessor));
    else localStorage.removeItem(`memorial_successor_${memorial.id}`);
  }, [currentSuccessor, memorial.id]);

  useEffect(() => {
    if (currentExecutor) localStorage.setItem(`memorial_executor_${memorial.id}`, JSON.stringify(currentExecutor));
    else localStorage.removeItem(`memorial_executor_${memorial.id}`);
  }, [currentExecutor, memorial.id]);

  useEffect(() => {
    localStorage.setItem(`memorial_assets_${memorial.id}`, JSON.stringify(assets));
  }, [assets, memorial.id]);

  useEffect(() => {
    const handleActivity = (event) => {
      const { type, author, count, timestamp } = event.detail;
      const newEntry = {
        id: Date.now(), type,
        detail: type === 'tribute' ? `New tribute from ${author}` : `${count} new photo(s) uploaded`,
        timestamp, subscriberCount: subscribers.length
      };
      setActivityLog(prev => [newEntry, ...prev].slice(0, 10));
    };
    window.addEventListener('memorial_activity', handleActivity);
    return () => window.removeEventListener('memorial_activity', handleActivity);
  }, [subscribers.length]);

  const resetDeleteState = () => { setShowDeleteConfirm(false); setDeleteStep(1); setIsDeleting(false); setConfirmName(''); };
  const handleDelete = () => {
    setIsDeleting(true);
    setTimeout(() => { window.location.href = '/#/explore'; }, 2500);
  };

  const handleAddSubscriber = (e) => {
    e.preventDefault();
    setEmailError('');
    if (!newEmail) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) { setEmailError('Please enter a valid email address'); return; }
    if (subscribers.includes(newEmail)) { setEmailError('This email is already on the subscriber list'); return; }
    if (subscribers.length >= 15) { setEmailError('Maximum limit of 15 notification subscribers reached'); return; }
    setSubscribers([...subscribers, newEmail]);
    setNewEmail('');
  };

  const handleRemoveSubscriber = (emailToRemove) => setSubscribers(subscribers.filter(e => e !== emailToRemove));

  const handleAssignSuccessor = (e) => {
    e.preventDefault();
    if (!successorEmail) return;
    setIsSendingInvite(true);
    setTimeout(() => {
      setIsSendingInvite(false); setInviteSent(true);
      setCurrentSuccessor({ email: successorEmail, status: 'Pending', assignedAt: new Date().toISOString() });
      setTimeout(() => { setInviteSent(false); setShowSuccessorModal(false); setSuccessorEmail(''); }, 2000);
    }, 1500);
  };

  const handleAssignExecutor = (e) => {
    e.preventDefault();
    if (!executorEmail) return;
    setIsSendingInvite(true);
    setTimeout(() => {
      setIsSendingInvite(false); setInviteSent(true);
      setCurrentExecutor({ email: executorEmail, status: 'Pending', assignedAt: new Date().toISOString() });
      setTimeout(() => { setInviteSent(false); setShowExecutorModal(false); setExecutorEmail(''); }, 2000);
    }, 1500);
  };

  const handleVerifyExecutor = () => { if (currentExecutor) setCurrentExecutor({ ...currentExecutor, status: 'Verified' }); };
  const handleRemoveSuccessor = () => { if (window.confirm('Remove the current successor?')) setCurrentSuccessor(null); };
  const handleRemoveExecutor = () => { if (window.confirm('Remove the current executor?')) setCurrentExecutor(null); };

  const isNameMatched = confirmName.trim().toLowerCase() === memorial.name.toLowerCase();
  const planTier = retentionPlan.toLowerCase();
  const hasAIGuidance = ['premium', 'executive', 'eternal'].includes(planTier);
  const hasFullTransfer = ['executive', 'eternal'].includes(planTier);

  return (
    <div className="space-y-12 pb-20">
      <LegacyPlanComparison currentPlan={retentionPlan} onUpgrade={(planId) => setRetentionPlan(planId)} />

      {/* ── TRIBUTE APPROVAL TOGGLE ── */}
      <TributeApprovalToggle memorial={memorial} />

      {/* ── CO-ADMIN ROLES ── */}
      <CoAdminRolesPanel memorial={memorial} />

      {/* ── Digital Asset Inventory ── */}
      <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark overflow-hidden relative">
        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/5 rounded-full -mr-24 -mt-24 blur-3xl" />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center shadow-sm">
              <SafeIcon icon={FiDatabase} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Digital Asset Inventory</h3>
              <p className="text-sm text-text-muted font-light">Guided checklists for heirs to secure digital accounts and coordinate business succession.</p>
            </div>
          </div>
          {!currentExecutor && (
            <button onClick={() => setShowExecutorModal(true)} className="px-8 py-3 bg-blue-500 text-white rounded-xl text-sm uppercase tracking-widest font-black flex items-center gap-2 hover:bg-blue-600 transition-all shadow-lg shadow-blue-100">
              <SafeIcon icon={FiUserPlus} /> Invite Executor
            </button>
          )}
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-6">
            <div className={`p-6 rounded-[32px] border-2 transition-all ${hasAIGuidance ? 'bg-white border-brand-dark' : 'bg-brand-light/20 border-brand-dark border-dashed opacity-75'}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${hasAIGuidance ? 'bg-accent-gold/10 text-accent-gold' : 'bg-brand-dark text-text-muted'}`}>
                  <SafeIcon icon={FiCpu} className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy">AI Legacy Guidance</h4>
                    {!hasAIGuidance && <span className="text-[8px] font-black uppercase tracking-widest px-2 py-0.5 bg-accent-gold text-white rounded-full">Premium</span>}
                  </div>
                  <p className="text-xs text-text-muted leading-relaxed font-light">Receive intelligent recommendations on how to categorize and protect digital assets.</p>
                  {hasAIGuidance && (
                    <button className="mt-4 text-[9px] font-black uppercase tracking-[0.2em] text-accent-gold flex items-center gap-2 hover:translate-x-1 transition-transform">
                      Activate Guide <SafeIcon icon={FiIcons.FiChevronRight} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-[32px] border-2 transition-all ${hasFullTransfer ? 'bg-white border-brand-dark' : 'bg-brand-light/20 border-brand-dark border-dashed opacity-75'}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${hasFullTransfer ? 'bg-blue-50 text-blue-500' : 'bg-brand-dark text-text-muted'}`}>
                  <SafeIcon icon={FiBriefcase} className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Full Transfer Services</h4>
                    {!hasFullTransfer && <span className="text-[8px] font-black uppercase tracking-widest px-2 py-0.5 bg-blue-500 text-white rounded-full">Executive</span>}
                  </div>
                  <p className="text-xs text-text-muted leading-relaxed font-light">Professional coordination for the seamless transfer of legal documents and high-value digital files.</p>
                  {hasFullTransfer && (
                    <button onClick={() => setIsAssetManagerOpen(true)} className="mt-4 text-[9px] font-black uppercase tracking-[0.2em] text-blue-500 flex items-center gap-2 hover:translate-x-1 transition-transform">
                      Review Inventory <SafeIcon icon={FiIcons.FiChevronRight} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-accent-navy/5 p-6 rounded-[32px] border border-brand-dark">
              <div className="flex items-center gap-3 mb-4">
                <SafeIcon icon={FiLock} className="text-accent-navy w-4 h-4" />
                <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Security Protocol</h4>
              </div>
              <p className="text-[11px] text-text-muted leading-relaxed italic">All inventory items are secured with zero-knowledge encryption. Even OneFinalLegacy cannot access the contents without authorization.</p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-brand-light/30 border-2 border-brand-dark rounded-[32px] p-8 h-full flex flex-col">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-navy/40 mb-6">Assigned Executor</h4>
              {currentExecutor ? (
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white border-2 border-brand-dark rounded-2xl flex items-center justify-center text-blue-500 shadow-sm">
                      <SafeIcon icon={FiUser} className="w-7 h-7" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-accent-navy">{currentExecutor.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${currentExecutor.status === 'Pending' ? 'bg-amber-400 animate-pulse' : 'bg-green-500'}`} />
                        <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">{currentExecutor.status}</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-6 border-t border-brand-dark/20">
                    {currentExecutor.status === 'Pending' && (
                      <button onClick={handleVerifyExecutor} className="col-span-2 py-3 bg-accent-navy text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all flex items-center justify-center gap-2 mb-2">
                        <SafeIcon icon={FiCheck} className="w-3 h-3" /> Verify Executor (Demo)
                      </button>
                    )}
                    <button onClick={() => setShowExecutorModal(true)} className="py-3 bg-white border border-brand-dark rounded-xl text-[9px] font-black uppercase tracking-widest text-accent-navy hover:bg-brand-light transition-all flex items-center justify-center gap-2">
                      <SafeIcon icon={FiRefreshCw} className="w-3 h-3" /> Change
                    </button>
                    <button onClick={handleRemoveExecutor} className="py-3 bg-white border border-brand-dark rounded-xl text-[9px] font-black uppercase tracking-widest text-red-500 hover:bg-red-50 transition-all flex items-center justify-center gap-2">
                      <SafeIcon icon={FiTrash2} className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-grow flex flex-col items-center justify-center text-center py-10">
                  <div className="w-16 h-16 bg-white border-2 border-brand-dark rounded-[24px] flex items-center justify-center text-blue-200 mb-6">
                    <SafeIcon icon={FiUserPlus} className="w-8 h-8" />
                  </div>
                  <p className="text-xs text-text-muted font-light leading-relaxed mb-8 max-w-[200px]">No executor assigned to manage your digital inventory yet.</p>
                  <button onClick={() => setShowExecutorModal(true)} className="text-[10px] font-black uppercase tracking-widest text-blue-500 hover:text-blue-600 transition-colors">Send Invitation</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Executor verified — full inventory panel */}
      {currentExecutor && currentExecutor.status === 'Verified' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-accent-navy text-white p-8 md:p-12 rounded-[40px] shadow-xl border border-brand-dark overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                <SafeIcon icon={FiDatabase} className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-serif mb-2">Asset Inventory Management</h3>
                <p className="text-white/70 text-sm font-light max-w-md">Executor access granted. Manage, categorize, and prepare the digital asset inventory.</p>
              </div>
            </div>
            <button onClick={() => setIsAssetManagerOpen(true)} className="px-10 py-4 bg-accent-gold text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-white transition-all shadow-xl shadow-accent-gold/20 flex items-center gap-3">
              Open Management Vault <SafeIcon icon={FiIcons.FiChevronRight} />
            </button>
          </div>
        </motion.div>
      )}

      {/* Notification Subscribers */}
      <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark overflow-hidden relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-accent-gold/5 rounded-full -mr-16 -mt-16 blur-3xl" />
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-accent-gold/10 rounded-2xl flex items-center justify-center text-accent-gold shadow-sm">
            <SafeIcon icon={FiBell} className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-2xl font-serif text-accent-navy">Notification Subscribers</h3>
            <p className="text-sm text-text-muted font-light italic">"Keep your designated circle informed of new public tributes and shared photos."</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <p className="text-sm text-text-muted leading-relaxed mb-6">
              Manage the list of recipients who receive real-time alerts for <strong>public tributes and photo uploads only</strong>.
            </p>
            <form onSubmit={handleAddSubscriber} className="space-y-3">
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input type="email" value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); if (emailError) setEmailError(''); }}
                  placeholder="subscriber@email.com"
                  className={`w-full pl-14 pr-28 py-4 bg-brand-light border-2 rounded-2xl outline-none transition-all text-sm font-bold ${emailError ? 'border-red-400' : 'border-brand-dark focus:border-accent-gold'}`}
                />
                <button type="submit" className="absolute right-2 top-2 bottom-2 bg-accent-navy text-white px-5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-colors flex items-center gap-2">
                  <SafeIcon icon={FiPlus} /> Add
                </button>
              </div>
              {emailError && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] text-red-500 font-black uppercase tracking-wider ml-1">{emailError}</motion.p>}
            </form>

            <div className="mt-10">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-navy/40 mb-4 flex items-center gap-2">
                <SafeIcon icon={FiActivity} className="w-3 h-3" /> Recent Alerts Dispatched
              </h4>
              <div className="space-y-3">
                {activityLog.length > 0 ? (
                  activityLog.map((log) => (
                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} key={log.id}
                      className="bg-brand-light/40 p-3 rounded-xl border border-brand-dark flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${log.type === 'tribute' ? 'bg-accent-gold/10 text-accent-gold' : 'bg-accent-navy/10 text-accent-navy'}`}>
                          <SafeIcon icon={log.type === 'tribute' ? FiIcons.FiMessageCircle : FiIcons.FiImage} className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-[11px] font-bold text-accent-navy line-clamp-1">{log.detail}</p>
                          <p className="text-[9px] text-text-muted font-medium uppercase tracking-widest">
                            {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • Sent to {log.subscriberCount} recp.
                          </p>
                        </div>
                      </div>
                      <div className="w-2 h-2 bg-green-500 rounded-full shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-6 border-2 border-dashed border-brand-dark rounded-2xl opacity-40">
                    <p className="text-[10px] font-black uppercase tracking-widest">No recent activity logged</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-brand-light/20 rounded-[32px] border border-brand-dark p-6 min-h-[200px] flex flex-col">
            <div className="flex items-center justify-between mb-4 px-2">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-navy/40">Active Subscribers ({subscribers.length}/15)</h4>
              {subscribers.length > 0 && (
                <span className="text-[9px] font-black text-green-600 uppercase tracking-widest flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" /> Live
                </span>
              )}
            </div>
            {subscribers.length > 0 ? (
              <div className="space-y-2 flex-grow overflow-y-auto max-h-[340px] pr-2">
                <AnimatePresence initial={false}>
                  {subscribers.map((email) => (
                    <motion.div key={email} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                      className="flex items-center justify-between bg-white p-4 rounded-2xl border border-brand-dark/50 group hover:border-accent-gold transition-all"
                    >
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className="w-8 h-8 rounded-lg bg-accent-navy/5 flex items-center justify-center text-accent-navy shrink-0">
                          <SafeIcon icon={FiUser} className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-bold text-accent-navy truncate">{email}</span>
                      </div>
                      <button onClick={() => handleRemoveSubscriber(email)} className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                        <SafeIcon icon={FiX} className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex-grow flex flex-col items-center justify-center text-center opacity-40 py-10">
                <SafeIcon icon={FiMail} className="w-10 h-10 mb-4" />
                <p className="text-xs font-bold uppercase tracking-widest">No subscribers added yet</p>
              </div>
            )}
            {subscribers.length > 0 && (
              <div className="mt-4 pt-4 border-t border-brand-dark">
                <button className="w-full text-[9px] font-black text-accent-navy hover:text-accent-gold uppercase tracking-widest transition-colors flex items-center justify-center gap-2">
                  <SafeIcon icon={FiSend} className="w-3 h-3" /> Send Test Alert to All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Succession Management */}
      <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark overflow-hidden relative">
        <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-accent-navy/5 rounded-full blur-3xl" />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-navy/10 rounded-2xl flex items-center justify-center text-accent-navy shadow-sm">
              <SafeIcon icon={FiUserPlus} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Succession Management</h3>
              <p className="text-sm text-text-muted font-light">Appoint a digital executor to protect this sanctuary's future.</p>
            </div>
          </div>
          {!currentSuccessor && (
            <button onClick={() => setShowSuccessorModal(true)} className="btn-secondary py-3 px-8 text-sm uppercase tracking-widest font-black flex items-center gap-2">
              <SafeIcon icon={FiPlus} /> Invite Successor
            </button>
          )}
        </div>

        {currentSuccessor ? (
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-brand-light/30 border-2 border-brand-dark rounded-[32px] p-6 relative overflow-hidden">
              <div className="absolute top-4 right-4 text-[8px] font-black uppercase tracking-widest px-2 py-1 bg-white border border-brand-dark rounded-full flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${currentSuccessor.status === 'Pending' ? 'bg-amber-400 animate-pulse' : 'bg-green-500'}`} />
                {currentSuccessor.status}
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-white border-2 border-brand-dark rounded-2xl flex items-center justify-center text-accent-navy">
                  <SafeIcon icon={FiUser} className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-0.5">Assigned Digital Heir</p>
                  <p className="text-sm font-bold text-accent-navy">{currentSuccessor.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-brand-dark/20">
                <button onClick={() => setShowSuccessorModal(true)} className="flex-grow py-3 bg-white border border-brand-dark rounded-xl text-[9px] font-black uppercase tracking-widest text-accent-navy hover:bg-brand-light transition-all flex items-center justify-center gap-2">
                  <SafeIcon icon={FiRefreshCw} className="w-3 h-3" /> Change Heir
                </button>
                <button onClick={handleRemoveSuccessor} className="p-3 bg-white border border-brand-dark rounded-xl text-text-muted hover:text-red-500 hover:border-red-200 transition-all">
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <div className="flex items-start gap-4 p-5 bg-accent-navy/[0.02] rounded-2xl border border-dashed border-brand-dark">
                <div className="w-10 h-10 shrink-0 bg-white rounded-xl flex items-center justify-center text-accent-gold shadow-sm">
                  <SafeIcon icon={FiUserCheck} className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-1">Succession Rights</h5>
                  <p className="text-[11px] text-text-muted leading-relaxed font-medium">The assigned successor will gain administrative control only if the primary creator is inactive for 12+ months, or through a verified legal request.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-10 bg-brand-light/20 rounded-[32px] border-2 border-dashed border-brand-dark flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-white border-2 border-brand-dark rounded-[24px] flex items-center justify-center text-accent-navy/20 mb-6">
              <SafeIcon icon={FiUserPlus} className="w-8 h-8" />
            </div>
            <h4 className="text-lg font-serif text-accent-navy mb-2">No Successor Assigned</h4>
            <p className="text-xs text-text-muted max-w-sm font-light leading-relaxed mb-8">Assigning a successor ensures that the legacy remains managed even after your own time.</p>
            <button onClick={() => setShowSuccessorModal(true)} className="py-4 px-10 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all shadow-lg shadow-accent-navy/10">
              Start Succession Plan
            </button>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="bg-red-50/30 p-8 md:p-12 rounded-[40px] border border-red-100">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center text-red-600 shadow-sm">
            <SafeIcon icon={FiAlertTriangle} className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-2xl font-serif text-red-900">Danger Zone</h3>
            <p className="text-sm text-red-700/60 font-light">Irreversible actions that affect this digital legacy.</p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-8 rounded-[32px] border border-red-100 shadow-sm">
          <div>
            <h4 className="font-bold text-red-900 mb-1">Delete Memorial Sanctuary</h4>
            <p className="text-xs text-red-700/60 leading-relaxed max-w-sm">Permanently remove all stories, photos, and community tributes. This action cannot be undone.</p>
          </div>
          <button onClick={() => setShowDeleteConfirm(true)} className="bg-red-500 text-white px-8 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-red-600 transition-colors flex items-center gap-2 shadow-lg shadow-red-200">
            <SafeIcon icon={FiTrash2} /> Erase Everything
          </button>
        </div>
      </div>

      {/* Delete Confirm Portal */}
      {showDeleteConfirm && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-red-950/40 backdrop-blur-md">
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden border border-red-100">
            <div className="p-10 text-center">
              <div className="relative mb-8">
                <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                  <SafeIcon icon={FiAlertTriangle} className="w-10 h-10" />
                </div>
                <button onClick={resetDeleteState} className="absolute top-0 right-0 p-2 hover:bg-brand-light rounded-full text-text-muted">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              <AnimatePresence mode="wait">
                {deleteStep === 1 ? (
                  <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <h2 className="text-3xl font-serif text-red-900 mb-4">Are you absolutely sure?</h2>
                    <p className="text-text-muted font-light mb-10 leading-relaxed">Deleting this sanctuary will erase all <span className="font-bold text-red-600">tributes, photos, and memories</span>. This action is irreversible.</p>
                    <div className="flex flex-col gap-3">
                      <button onClick={() => setDeleteStep(2)} className="w-full bg-red-600 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-red-700 transition-colors">I understand, proceed</button>
                      <button onClick={resetDeleteState} className="w-full py-4 text-text-muted font-bold hover:text-accent-navy transition-colors">Cancel and keep memories</button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <div className="bg-red-50 p-6 rounded-2xl mb-8 flex items-start gap-4 text-left border border-red-100">
                      <SafeIcon icon={FiAlertCircle} className="text-red-600 w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-red-900 leading-relaxed font-bold uppercase tracking-wider mb-2">Final Confirmation</p>
                        <p className="text-sm text-red-800 leading-relaxed mb-4">Please type the full name of the memorial to confirm:</p>
                        <p className="font-black text-lg text-red-900 select-all mb-4 italic">"{memorial.name}"</p>
                      </div>
                    </div>
                    <div className="mb-8">
                      <input type="text" autoFocus placeholder="Type memorial name here"
                        className={`w-full px-6 py-5 bg-brand-light border-2 rounded-2xl outline-none transition-all text-center font-bold text-accent-navy text-lg ${isNameMatched ? 'border-green-500 bg-green-50' : 'border-brand-dark focus:border-red-500'}`}
                        value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                      />
                    </div>
                    <button onClick={handleDelete} disabled={!isNameMatched || isDeleting}
                      className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${isNameMatched ? 'bg-red-600 text-white hover:bg-red-700 shadow-xl shadow-red-200' : 'bg-brand-dark text-text-muted cursor-not-allowed'}`}
                    >
                      {isDeleting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Erasing Sanctuary…</span></> : <><SafeIcon icon={FiTrash2} /> Permanently Delete Everything</>}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Successor Modal */}
      {showSuccessorModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[40px] shadow-2xl w-full max-w-md overflow-hidden border border-brand-dark">
            <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent-navy text-white rounded-xl flex items-center justify-center"><SafeIcon icon={FiUserPlus} /></div>
                <h3 className="font-serif text-xl text-accent-navy">Assign Successor</h3>
              </div>
              <button onClick={() => setShowSuccessorModal(false)} className="p-2 hover:bg-white rounded-full text-text-muted transition-colors"><SafeIcon icon={FiX} /></button>
            </div>
            <form onSubmit={handleAssignSuccessor} className="p-8 space-y-6">
              {inviteSent ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4"><SafeIcon icon={FiCheck} className="w-8 h-8" /></div>
                  <h4 className="text-xl font-serif text-accent-navy mb-2">Invitation Sent</h4>
                  <p className="text-sm text-text-muted">They will receive an email shortly to accept the transfer of administration.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-text-muted font-light leading-relaxed">Enter the email address of the person you wish to appoint as the digital heir.</p>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input required type="email" placeholder="Successor's Email Address" className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all" value={successorEmail} onChange={e => setSuccessorEmail(e.target.value)} />
                  </div>
                  <button type="submit" disabled={isSendingInvite || !successorEmail} className="w-full btn-primary py-4 flex items-center justify-center gap-2">
                    {isSendingInvite ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Send Invitation'}
                  </button>
                </>
              )}
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Executor Modal */}
      {showExecutorModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-blue-950/60 backdrop-blur-md">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[40px] shadow-2xl w-full max-w-md overflow-hidden border border-brand-dark">
            <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 text-white rounded-xl flex items-center justify-center"><SafeIcon icon={FiUserPlus} /></div>
                <h3 className="font-serif text-xl text-accent-navy">Invite Executor</h3>
              </div>
              <button onClick={() => setShowExecutorModal(false)} className="p-2 hover:bg-white rounded-full text-text-muted transition-colors"><SafeIcon icon={FiX} /></button>
            </div>
            <form onSubmit={handleAssignExecutor} className="p-8 space-y-6">
              {inviteSent ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4"><SafeIcon icon={FiCheck} className="w-8 h-8" /></div>
                  <h4 className="text-xl font-serif text-accent-navy mb-2">Invitation Sent</h4>
                  <p className="text-sm text-text-muted">An invitation has been sent to the designated executor.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-text-muted font-light leading-relaxed">Assign an executor to manage the Digital Asset Inventory.</p>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input required type="email" placeholder="Executor's Email Address" className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-blue-500 outline-none text-sm transition-all" value={executorEmail} onChange={e => setExecutorEmail(e.target.value)} />
                  </div>
                  <button type="submit" disabled={isSendingInvite || !executorEmail} className="w-full bg-blue-500 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-blue-600 transition-all shadow-lg shadow-blue-100 flex items-center justify-center gap-2">
                    {isSendingInvite ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Send Invitation'}
                  </button>
                </>
              )}
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      <DigitalAssetManagerModal isOpen={isAssetManagerOpen} onClose={() => setIsAssetManagerOpen(false)} assets={assets} setAssets={setAssets} successor={currentSuccessor} />
    </div>
  );
};

export default TabLegacySettings;